import Phaser from 'phaser';
import { TILE_SIZE, VIEWPORT_TILES, globalPortalRegistry } from '@blockworld/shared';
import { GameClient } from './network/GameClient';
import { GameScene } from './scenes/GameScene';

// ============================================================
// MAIN ENTRY POINT
// ============================================================

// Performance timing baseline — all [PERF] logs are relative to this (console only)
const _perfStart = performance.now();
const _perf = (label: string) => {
    console.log(`[PERF] +${(performance.now() - _perfStart).toFixed(0)}ms ${label}`);
};
(window as any).__perf = _perf;

_perf('main.ts module executing');

// Get server URL from environment or default
const SERVER_URL = import.meta.env.VITE_SERVER_URL || 'ws://localhost:2567';
const IS_DEV_SERVER = SERVER_URL.includes('localhost') || SERVER_URL.includes('127.0.0.1');
if (IS_DEV_SERVER) document.title = '[DEV] BlockWorld';

// Override default portal destination when a custom server URL is configured
if (import.meta.env.VITE_SERVER_URL) {
    globalPortalRegistry.setDefault({
        worldId: 'shared',
        isRemote: true,
        serverUrl: import.meta.env.VITE_SERVER_URL,
        spawnX: 0,
    });
}

// Check if online mode is requested via URL param
const urlParams = new URLSearchParams(window.location.search);
const forceOnline = urlParams.get('online') === 'true';

// Persistent device identity — survives page refreshes and reconnections
function generateUUID(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    // Fallback for older browsers: crypto.getRandomValues is more widely supported
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 1
    const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
function getOrCreateDeviceId(): string {
    const STORAGE_KEY = 'blockworld_device_id';
    try {
        const existing = localStorage.getItem(STORAGE_KEY);
        if (existing) return existing;
        const id = generateUUID();
        localStorage.setItem(STORAGE_KEY, id);
        return id;
    } catch {
        // Private browsing or no localStorage — generate ephemeral ID
        return generateUUID();
    }
}
const DEVICE_ID = getOrCreateDeviceId();

// UI Elements
const connectionStatus = document.getElementById('connection-status')!;
const playerCount = document.getElementById('player-count')!;

// Create unified game client
const gameClient = new GameClient();
gameClient.deviceId = DEVICE_ID;

// Setup connection status callbacks
gameClient.onConnectionChange = (status) => {
    switch (status) {
        case 'connecting':
            connectionStatus.className = 'disconnected';
            connectionStatus.textContent = 'Connecting...';
            break;
        case 'connected':
            const mode = gameClient.getMode();
            if (mode === 'offline') {
                connectionStatus.className = 'disconnected';
                connectionStatus.textContent = 'Offline Mode';
            } else if (IS_DEV_SERVER) {
                connectionStatus.className = 'dev';
                connectionStatus.textContent = 'DEV SERVER';
            } else {
                connectionStatus.className = 'connected';
                connectionStatus.textContent = 'Connected';
            }
            break;
        case 'disconnected':
            connectionStatus.className = 'disconnected';
            connectionStatus.textContent = 'Disconnected';
            break;
        case 'error':
            connectionStatus.className = 'disconnected';
            connectionStatus.textContent = 'Connection Error';
            break;
    }
};

gameClient.onPlayerCountChange = (count) => {
    playerCount.textContent = `Players: ${count}`;
};

// Native game resolution
const GAME_WIDTH = VIEWPORT_TILES * TILE_SIZE;
const GAME_HEIGHT = VIEWPORT_TILES * TILE_SIZE;
const BUFFER = 40; // Buffer from window edges (desktop only)

// Detect mobile via screen size + touch support
function isMobileDevice(): boolean {
    return ('ontouchstart' in window || navigator.maxTouchPoints > 0)
        && (window.innerWidth <= 768 || window.innerHeight <= 768);
}

// Calculate scale to fit window
function calculateScale(): number {
    if (isMobileDevice()) {
        const isPortrait = window.innerHeight > window.innerWidth;
        if (isPortrait) {
            // Fill full width
            return window.innerWidth / GAME_WIDTH;
        } else {
            // Fill full height
            return window.innerHeight / GAME_HEIGHT;
        }
    }

    // Desktop: integer scale with buffer
    const maxWidth = window.innerWidth - BUFFER;
    const maxHeight = window.innerHeight - BUFFER;
    const scaleX = Math.floor(maxWidth / GAME_WIDTH);
    const scaleY = Math.floor(maxHeight / GAME_HEIGHT);
    return Math.max(1, Math.min(scaleX, scaleY));
}

// Create Phaser game config
const config: Phaser.Types.Core.GameConfig = {
    type: Phaser.AUTO,
    width: GAME_WIDTH,
    height: GAME_HEIGHT,
    parent: 'game-container',
    backgroundColor: '#000000',  // Black to match splash; sky-blue set when gameplay starts
    pixelArt: true,
    render: {
        antialias: false,
        pixelArt: true,
        roundPixels: true,
    },
    scale: {
        mode: Phaser.Scale.NONE, // We handle scaling manually
    },
    scene: new GameScene(gameClient),
};

// Initialize game
_perf('Phaser.Game constructor starting');
const game = new Phaser.Game(config);
_perf('Phaser.Game constructor returned');

// Apply initial scale
function applyScale() {
    const scale = calculateScale();
    const canvas = game.canvas;
    if (canvas) {
        canvas.style.width = `${GAME_WIDTH * scale}px`;
        canvas.style.height = `${GAME_HEIGHT * scale}px`;
        canvas.style.imageRendering = 'pixelated';
        canvas.style.imageRendering = 'crisp-edges';
    }
}

// Re-apply scale on window resize
window.addEventListener('resize', applyScale);

// --- HTML splash screen dismiss ---
// The splash is a pure HTML overlay that appears instantly before Phaser loads.
// On any interaction, record it and tell the game scene — but DON'T hide the splash yet.
// The game scene will hide it once the title screen has rendered, preventing blank frames.
const splash = document.getElementById('splash');
let splashDismissed = false;

function dismissSplash() {
    if (splashDismissed) return;
    splashDismissed = true;
    _perf('Splash dismissed (user interaction)');
    // Mark as dismissed (GameScene checks this if create() runs after the click)
    if (splash) splash.dataset.dismissed = 'true';
    // Signal the game scene that the user has interacted
    game.events.emit('splash-dismissed');
}

// The game scene hides the splash once the title screen is ready to display
game.events.on('hide-splash', () => {
    _perf('hide-splash event — splash overlay hidden');
    if (splash) splash.classList.add('hidden');
});

if (splash) {
    splash.addEventListener('click', dismissSplash);
    splash.addEventListener('touchstart', dismissSplash);
    document.addEventListener('keydown', function onKey() {
        dismissSplash();
        document.removeEventListener('keydown', onKey);
    });
}

// Start in offline mode by default, or online if requested
async function startGame() {
    if (forceOnline) {
        try {
            await gameClient.connectOnline(SERVER_URL);
        } catch (error) {
            console.error('Failed to connect to server, falling back to offline:', error);
            connectionStatus.textContent = 'Server unavailable - Offline Mode';
            connectionStatus.className = 'disconnected';
            await gameClient.connectOffline();
        }
    } else {
        // Default: start in offline/local mode
        await gameClient.connectOffline();
    }
}

// Apply scale once Phaser is ready
game.events.once('ready', () => {
    _perf('Phaser "ready" event fired');
    applyScale();
});

// Wait for GameScene.create() to complete before starting connection
// The scene emits 'scene-ready' after all callbacks are set up
game.events.once('scene-ready', () => {
    _perf('"scene-ready" event fired — starting game connection');

    startGame().then(() => {
        _perf('startGame() resolved (connection established)');
    }).catch((error) => {
        console.error('Failed to start game:', error);
        connectionStatus.textContent = 'Failed to start game';
        connectionStatus.className = 'disconnected';
    });
});

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
    gameClient.disconnect();
});
